import React, { useEffect } from 'react';
import { CheckCircle, Phone, Mail, Calendar, Clock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CountUp } from '../components/CountUp';
import { getCurrentStats } from '../utils/autoIncrementStats';
import config from '../config';
import { trackBookingCompleted, trackPhoneClick, trackEmailClick } from '../analytics';

export const ThankYou = () => {
  const currentStats = getCurrentStats();

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Track booking completion in Google Analytics
    trackBookingCompleted();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#28BCB7]/5 via-white to-[#2C60AC]/5">
      {/* Header with Logo */}
      <header className="bg-white shadow-sm py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={config.assets.logos.horizontalLarge}
            alt={config.company.name}
            className="h-32 md:h-40 mx-auto"
          />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Success Icon */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full mb-6 shadow-xl">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#303030] mb-4 leading-tight">
            Thank You for Choosing<br />
            <span className="text-[#28BCB7]">{config.company.name}!</span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-2">
            Your cleaning service request has been received successfully.
          </p>
          
          <p className="text-lg text-[#2C60AC] font-semibold">
            We're excited to make your space sparkle! ✨
          </p>
        </div>

        {/* Main Card */}
        <Card className="mb-8 border-0 shadow-2xl animate-slide-up">
          <CardContent className="p-8 md:p-12">
            <div className="space-y-8">
              {/* What Happens Next */}
              <div>
                <h2 className="text-2xl font-bold text-[#303030] mb-6 flex items-center">
                  <Calendar className="h-6 w-6 text-[#28BCB7] mr-3" />
                  What Happens Next?
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#28BCB7] text-white rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#303030] text-lg">Confirmation Call</h3>
                      <p className="text-gray-600">
                        Our team will contact you within <span className="font-semibold text-[#2C60AC]">24 hours</span> to confirm your appointment details and answer any questions you may have.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#28BCB7] text-white rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#303030] text-lg">Schedule Verification</h3>
                      <p className="text-gray-600">
                        We'll verify your preferred date, time, and specific cleaning requirements to ensure everything is perfect.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#28BCB7] text-white rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#303030] text-lg">Professional Service</h3>
                      <p className="text-gray-600">
                        Our experienced team will arrive on time with all necessary equipment and eco-friendly products to transform your space.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200"></div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-[#303030] mb-6 flex items-center">
                  <Phone className="h-6 w-6 text-[#28BCB7] mr-3" />
                  Need to Reach Us?
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Call us directly</p>
                      <a href={`tel:${config.contact.phone}`} className="text-lg font-semibold text-[#2C60AC] hover:text-[#28BCB7] transition-colors" onClick={trackPhoneClick}>
                        {config.contact.phoneDisplay}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email us</p>
                      <a href={`mailto:${config.contact.email}`} className="text-lg font-semibold text-[#2C60AC] hover:text-[#28BCB7] transition-colors break-all" onClick={trackEmailClick}>
                        {config.contact.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-gradient-to-r from-[#28BCB7]/10 to-[#2C60AC]/10 rounded-lg p-6 text-center">
                <Clock className="h-8 w-8 text-[#28BCB7] mx-auto mb-3" />
                <p className="text-[#303030] font-semibold text-lg mb-2">
                  Quick Response Guarantee
                </p>
                <p className="text-gray-600">
                  We understand your time is valuable. You'll hear from us within 24 hours to confirm your appointment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Why You Made the Right Choice */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8 animate-slide-up" style={{ animationDelay: '200ms' }}>
          <h2 className="text-2xl font-bold text-[#303030] mb-6 text-center flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-[#28BCB7] mr-3" />
            Why You Made the Right Choice
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#28BCB7] mb-2">
                <CountUp end={currentStats.clients} suffix="+" />
              </div>
              <p className="text-gray-600">Happy Clients</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-[#28BCB7] mb-2">
                <CountUp end={currentStats.spaces} suffix="+" />
              </div>
              <p className="text-gray-600">Spaces Cleaned</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-[#28BCB7] mb-2">
                <CountUp end={currentStats.satisfaction} suffix="%" />
              </div>
              <p className="text-gray-600">Satisfaction Rate</p>
            </div>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="text-center">
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-[#2C60AC] hover:bg-[#28BCB7] text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            Return to Home
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#303030] text-white py-8 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={config.assets.logos.negative}
            alt={config.company.name}
            className="h-10 mx-auto mb-4"
          />
          <p className="text-gray-300 text-sm">
            {config.company.description} • {config.company.locations}
          </p>
          <p className="text-gray-400 text-xs mt-2">
            © {new Date().getFullYear()} {config.company.name}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
